/**
 * Admin Auth Provider
 *
 * Manages admin authentication state using HTTP-only session cookies.
 * Authentication is handled via SwiftGate SSO (OIDC).
 *
 * Security:
 * - Authentication is managed via HTTP-only cookies (not accessible via JS)
 * - Sessions persist across page refreshes
 * - Protected against XSS attacks since cookies are not accessible via JavaScript
 */

import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { toast } from 'sonner';
import { adminApiClient, sessionExpiredEmitter } from '../api/adminClient';
import { apiUrl, responseError } from '../api/apiBase';
import { onTenantSwitchElsewhere, reloadIntoCurrentTenant, setTenantPin } from '../api/tenantPin';
import { actionFailedMessage } from '../components/inputs/AsyncArea/copy';
import { logger } from '../utils/logger';
import type { AdminUser } from '../types/admin';
import { AdminAuthContext, type AdminAuthContextType } from './adminAuthContextDef';

interface AdminAuthProviderProps {
  children: ReactNode;
}

export const AdminAuthProvider = ({ children }: AdminAuthProviderProps) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<string | null>(null);
  const [serverClockOffsetMs, setServerClockOffsetMs] = useState(0);

  // Synchronous auth snapshot for TanStack Router's beforeLoad guard.
  // React state updates are asynchronous, so the router can read a stale
  // isAuthenticated value between setUser() and the next render commit.
  // This ref is updated in the same tick as setUser(), giving the guard
  // an immediately-consistent value to check.
  const isAuthenticatedRef = useRef(false);
  const userRef = useRef<AdminUser | null>(null);

  // One-time promise that resolves when the initial session check completes.
  // useMemo with [] creates both the promise and its resolver together in a
  // closure — no type casting needed.
  const { ready, resolveReady } = useMemo(() => {
    let resolveReady!: (isAuthenticated: boolean) => void;
    const ready = new Promise<boolean>((resolve) => { resolveReady = resolve; });
    return { ready, resolveReady };
  }, []);

  /**
   * Check for existing session on mount.
   * Uses HTTP-only cookie authentication - cookies are sent automatically by browser.
   */
  const checkSession = useCallback(async () => {
    let isAuthenticated = false;
    try {
      const sessionData = await adminApiClient.checkSession();

      if (sessionData.authenticated && sessionData.user) {
        isAuthenticatedRef.current = true;
        userRef.current = sessionData.user;
        setTenantPin(sessionData.user.tenant_id);
        setUser(sessionData.user);
        setSessionExpiresAt(sessionData.expiresAt ?? null);
        // A switch's deadline is the server's; time it by the server's clock.
        const serverTime = sessionData.serverTime ? Date.parse(sessionData.serverTime) : NaN;
        setServerClockOffsetMs(Number.isFinite(serverTime) ? serverTime - Date.now() : 0);
        isAuthenticated = true;
      } else {
        isAuthenticatedRef.current = false;
        userRef.current = null;
        setUser(null);
        setSessionExpiresAt(null);
      }
    } catch (error) {
      // Session check failed - user is not authenticated
      logger.error('Session check failed', error);
      isAuthenticatedRef.current = false;
      userRef.current = null;
      setUser(null);
      setSessionExpiresAt(null);
    } finally {
      setLoading(false);
      resolveReady(isAuthenticated);
    }
  }, [resolveReady]);

  // Check for existing session on mount
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // A platform admin switched tenant in another tab: this tab's session (the
  // cookie is shared) is now in another tenant, so nothing on screen is
  // current. Reload into it before anyone acts on the old tenant's data.
  useEffect(() => onTenantSwitchElsewhere(reloadIntoCurrentTenant), []);

  // When a request 401s mid-session, the axios interceptor emits "expired" and
  // redirects to /admin/login. Clear the auth snapshot immediately so the login
  // route's redirectIfAuthenticated guard (which reads isAuthenticatedRef) does
  // NOT bounce the user back to /admin — that stale-ref bounce was the "can't
  // escape the login page" loop.
  useEffect(() => {
    const onExpired = () => {
      isAuthenticatedRef.current = false;
      userRef.current = null;
      setUser(null);
      setSessionExpiresAt(null);
    };
    sessionExpiredEmitter.addEventListener('expired', onExpired);
    return () => sessionExpiredEmitter.removeEventListener('expired', onExpired);
  }, []);

  /**
   * Logout and clear the session.
   *
   * The local session and the upstream SwiftGate SSO session must both be
   * terminated for the user to be truly logged out. If we clear local
   * state but the server call fails, the upstream SSO session stays alive
   * and re-clicking "Log in" lets anyone bypass credentials via auto-SSO.
   *
   * So: only clear local state on a successful response — and only after
   * we have the redirect URL in hand. On failure, surface a toast and
   * leave the user signed in so they can retry.
   */
  const logout = async (): Promise<void> => {
    // POSITIVE check on authMethod: only hit the SwiftGate-aware logout
    // endpoint when we know for sure the current session came from
    // SwiftGate. Anything else (local-auth, null userRef on an unauthorized
    // page, unknown future authMethod values) defaults to the plain
    // /auth/logout endpoint. Picking the wrong endpoint for a local user
    // would redirect them to http://swiftgate.../oauth/rp-logout, which is
    // the bug we're fixing.
    const isSwiftGate = userRef.current?.authMethod === 'swiftgate';
    const endpoint = isSwiftGate
      ? apiUrl('/admin/auth/swiftgate/logout')
      : apiUrl('/admin/auth/logout');

    let response: Response;
    try {
      response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      logger.error('Logout request failed', error);
      toast.error(actionFailedMessage('sign out', error));
      return;
    }

    if (!response.ok) {
      logger.error('Logout endpoint returned non-OK', { status: response.status });
      toast.error(actionFailedMessage('sign out', await responseError(response)));
      return;
    }

    const data = await response.json().catch(() => ({} as { swiftgateLogoutUrl?: string }));

    isAuthenticatedRef.current = false;
    userRef.current = null;
    setUser(null);
    setSessionExpiresAt(null);

    if (data.swiftgateLogoutUrl) {
      window.location.href = data.swiftgateLogoutUrl;
      return;
    }

    // Provider has no logout URL configured, OR this was a local-auth user
    // — fall back to login page so the SPA does not leave the user staring
    // at admin chrome they no longer have a session for.
    window.location.href = '/admin/login?logged_out=true';
  };

  /**
   * Refresh the session (check if still valid and update user info)
   */
  const refreshSession = async (): Promise<void> => {
    await checkSession();
  };

  const value: AdminAuthContextType = {
    user,
    loading,
    sessionExpiresAt,
    serverClockOffsetMs,
    logout,
    isAuthenticated: !!user,
    isAuthenticatedRef,
    userRef,
    refreshSession,
    ready,
  };

  // React 19: Use Context directly instead of Context.Provider
  return <AdminAuthContext value={value}>{children}</AdminAuthContext>;
};
